// VertexTable gelişmiş filtre motoru (ClickUp "Filters" deseni) — saf, DOM'suz.
//
// Grup:  { logic: 'and' | 'or', rules: [Kural | Grup] }
// Kural: { id, acc, op, values: [], v, v2 }
// Grup (iç içe): { id, group: true, logic, rules: [...] }
//
// Değer okuyucu (getter) satırdan kolonun FİLTRE değerlerini verir — DataTable'ın huni
// filtresinin kullandığı fonksiyonların AYNISI (filterFormat/filterValues/filterNumber), yani
// "Durum = IFC" huni ile panelde aynı satırları bulur.

export const OPS = {
  values: [
    { k: 'is', l: 'Şunlardan biri' },
    { k: 'is_not', l: 'Şunlardan biri değil' },
    { k: 'empty', l: 'Boş' },
    { k: 'not_empty', l: 'Dolu' },
  ],
  text: [
    { k: 'contains', l: 'İçerir' },
    { k: 'not_contains', l: 'İçermez' },
    { k: 'eq', l: 'Eşittir' },
    { k: 'starts', l: 'İle başlar' },
    { k: 'empty', l: 'Boş' },
    { k: 'not_empty', l: 'Dolu' },
  ],
  range: [
    { k: 'eq', l: '=' },
    { k: 'neq', l: '≠' },
    { k: 'gt', l: '>' },
    { k: 'gte', l: '≥' },
    { k: 'lt', l: '<' },
    { k: 'lte', l: '≤' },
    { k: 'between', l: 'Arasında' },
    { k: 'empty', l: 'Boş' },
    { k: 'not_empty', l: 'Dolu' },
  ],
  daterange: [
    { k: 'on', l: 'Tarihinde' },
    { k: 'before', l: 'Öncesinde' },
    { k: 'after', l: 'Sonrasında' },
    { k: 'between', l: 'Arasında' },
    { k: 'empty', l: 'Boş' },
    { k: 'not_empty', l: 'Dolu' },
  ],
};

// Sunucu-sayfalı (kontrollü) tabloda yalnız kolon hunisinin ifade edebildiği koşullar — çünkü
// süzmeyi sayfa (sunucu) yapar ve elinde yalnız {values} / {text} / {min,max} vardır.
export const CONTROLLED_OPS = {
  values: ['is'],
  text: ['contains'],
  range: ['eq', 'gte', 'lte', 'between'],
  daterange: ['on', 'before', 'after', 'between'],
};

const NO_VALUE = new Set(['empty', 'not_empty']);

let _seq = 0;
export const newId = () => `f${Date.now().toString(36)}${(_seq++).toString(36)}`;
export const newRule = (acc = null, op = null) => ({ id: newId(), acc, op, values: [], v: '', v2: '' });
export const newGroup = (logic = 'and') => ({ id: newId(), group: true, logic, rules: [newRule()] });
export const emptyRoot = () => ({ logic: 'and', rules: [] });

export const defaultOp = (type) => (OPS[type] || OPS.values)[0].k;

const blank = (x) => x === undefined || x === null || String(x).trim() === '';

// Kural tamamlanmış mı (alan + koşul + gereken değer) — yarım kural süzmeye girmez.
export function ruleComplete(rule, type) {
  if (!rule || rule.group || !rule.acc || !rule.op) return false;
  if (NO_VALUE.has(rule.op)) return true;
  if (type === 'values') return (rule.values || []).length > 0;
  if (rule.op === 'between') return !blank(rule.v) && !blank(rule.v2);
  return !blank(rule.v);
}

// Grubun (özyinelemeli) tamamlanmış kural sayısı — düğme rozeti
export function countRules(node, typeOf) {
  if (!node) return 0;
  return (node.rules || []).reduce((n, r) => n + (r.group ? countRules(r, typeOf) : (ruleComplete(r, typeOf(r.acc)) ? 1 : 0)), 0);
}

const _lc = (s) => String(s ?? '').toLocaleLowerCase('tr').trim();
const _num = (s) => {
  if (blank(s)) return null;
  const n = Number(String(s).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const _day = (s) => {
  if (blank(s)) return null;
  const t = Date.parse(String(s).slice(0, 10));
  return Number.isFinite(t) ? t : null;
};

// get(acc) → { type, vals(row)→string[], text(row)→string, num(row)→number|null, date(row)→epoch|null }
export function evalRule(rule, row, g) {
  const op = rule.op;
  const t = g.type;
  if (t === 'values') {
    const vals = g.vals(row).filter((x) => x !== '');
    if (op === 'empty') return vals.length === 0;
    if (op === 'not_empty') return vals.length > 0;
    const hit = vals.some((x) => (rule.values || []).includes(x))
      || ((rule.values || []).includes('') && vals.length === 0);
    return op === 'is_not' ? !hit : hit;
  }
  if (t === 'text') {
    const s = _lc(g.text(row));
    if (op === 'empty') return s === '';
    if (op === 'not_empty') return s !== '';
    const q = _lc(rule.v);
    if (op === 'contains') return s.includes(q);
    if (op === 'not_contains') return !s.includes(q);
    if (op === 'eq') return s === q;
    if (op === 'starts') return s.startsWith(q);
    return true;
  }
  if (t === 'range') {
    const n = g.num(row);
    if (op === 'empty') return n === null;
    if (op === 'not_empty') return n !== null;
    if (n === null) return false;
    const a = _num(rule.v);
    const b = _num(rule.v2);
    if (op === 'eq') return n === a;
    if (op === 'neq') return n !== a;
    if (op === 'gt') return n > a;
    if (op === 'gte') return n >= a;
    if (op === 'lt') return n < a;
    if (op === 'lte') return n <= a;
    if (op === 'between') return n >= Math.min(a, b) && n <= Math.max(a, b);
    return true;
  }
  if (t === 'daterange') {
    const d = g.date(row);
    if (op === 'empty') return d === null;
    if (op === 'not_empty') return d !== null;
    if (d === null) return false;
    const a = _day(rule.v);
    const b = _day(rule.v2);
    const END = 86399999;   // gün sonu dahil
    if (op === 'on') return d >= a && d <= a + END;
    if (op === 'before') return d < a;
    if (op === 'after') return d > a + END;
    if (op === 'between') return d >= Math.min(a, b) && d <= Math.max(a, b) + END;
    return true;
  }
  return true;
}

// Grup değerlendirmesi — yarım kurallar ve boş alt gruplar yok sayılır (ClickUp davranışı).
export function evalGroup(node, row, getter) {
  const live = [];
  for (const r of node.rules || []) {
    if (r.group) {
      if (countRules(r, (acc) => getter(acc)?.type) > 0) live.push(r);
    } else {
      const g = getter(r.acc);
      if (g && ruleComplete(r, g.type)) live.push(r);
    }
  }
  if (!live.length) return true;
  const test = (r) => (r.group ? evalGroup(r, row, getter) : evalRule(r, row, getter(r.acc)));
  return node.logic === 'or' ? live.some(test) : live.every(test);
}

// ── Kontrollü mod: kural ↔ kolon hunisi nesnesi ──
export function ruleToColFilter(rule, type) {
  if (!ruleComplete(rule, type)) return null;
  if (type === 'values') return rule.op === 'is' ? { values: [...rule.values] } : null;
  if (type === 'text') return rule.op === 'contains' ? { text: String(rule.v) } : null;
  const a = rule.v;
  const b = rule.v2;
  if (rule.op === 'eq' || rule.op === 'on') return { min: a, max: a };
  if (rule.op === 'gte' || rule.op === 'after') return { min: a };
  if (rule.op === 'lte' || rule.op === 'before') return { max: a };
  if (rule.op === 'between') return { min: a, max: b };
  return null;
}

export function colFilterToRule(acc, f, type) {
  if (!f) return null;
  if (type === 'values') return (f.values || []).length ? { ...newRule(acc, 'is'), values: [...f.values] } : null;
  if (type === 'text') return f.text ? { ...newRule(acc, 'contains'), v: f.text } : null;
  const hasMin = !blank(f.min);
  const hasMax = !blank(f.max);
  if (hasMin && hasMax) {
    if (String(f.min) === String(f.max)) return { ...newRule(acc, type === 'daterange' ? 'on' : 'eq'), v: f.min };
    return { ...newRule(acc, 'between'), v: f.min, v2: f.max };
  }
  if (hasMin) return { ...newRule(acc, type === 'daterange' ? 'after' : 'gte'), v: f.min };
  if (hasMax) return { ...newRule(acc, type === 'daterange' ? 'before' : 'lte'), v: f.max };
  return null;
}

// Değer çipi rengi — değerden türetilir (aynı değer her yerde aynı renk). Literal sınıflar (JIT).
const CHIP = [
  'bg-violet-100 text-violet-700', 'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-800', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700',
  'bg-lime-100 text-lime-800', 'bg-fuchsia-100 text-fuchsia-700',
];
export function chipClass(value) {
  if (value === '' || value == null) return 'bg-gray-100 text-gray-500';
  let h = 0;
  const s = String(value);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CHIP[h % CHIP.length];
}
